import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { clsx } from 'clsx';
import {
  Rocket,
  Server,
  Box,
  Database,
  Monitor,
  Globe,
  ShieldCheck,
  Terminal,
  Info,
  AlertTriangle,
  Lightbulb,
  ChevronRight,
  Cloud,
  Compass,
  Network,
} from 'lucide-react';
import TourList, { TourProgressSummary } from '../tours/TourList';
import { Card, DataTable, PageHeader, Tabs, type DataTableColumn } from '@/components/ds-zta';

// Getting Started screenshots (imported so Vite hashes + caches them).
import imgSshServers from '../assets/getting-started/ssh-servers.webp';
import imgK8sAddCluster from '../assets/getting-started/k8s-add-cluster.webp';
import imgK8sResources from '../assets/getting-started/k8s-resources.webp';
import imgDesktopAdd from '../assets/getting-started/desktop-add.webp';
import imgDesktopConnect from '../assets/getting-started/desktop-connect.webp';
import imgProxyEdit from '../assets/getting-started/proxy-edit.webp';
import imgCloudProviders from '../assets/getting-started/cloud-providers.webp';
import imgCloudAddAws from '../assets/getting-started/cloud-add-aws.webp';
import imgDatabaseAdd from '../assets/getting-started/database-add.webp';
import { useUrlState } from '@/lib/useUrlState';
import { getTenant } from '../services/tenant';
import { BRAND } from '@/brand';
import CodeBlock from '@/components/CodeBlock';
import ReleaseDownload, { type ReleaseFile } from '@/components/ReleaseDownload';

/* ─────────────────────────────────────────────────────────────
   Reusable building blocks
   ───────────────────────────────────────────────────────────── */

// OSTabs renders one command per workstation OS behind a tab strip, so each OS gets
// its own copy button (via CodeBlock) instead of one mixed block. Only the OSes
// present in `commands` render a tab; the first available one is selected initially.
type OSKey = 'mac-arm' | 'mac-intel' | 'linux' | 'windows';

// Release files offered by the download steps (admin-api signs a link to one).
const CLI_FILES: ReleaseFile[] = [
  { file: 'iap-darwin-arm64', label: 'macOS · Apple Silicon', saveAs: 'iap' },
  { file: 'iap-darwin-amd64', label: 'macOS · Intel', saveAs: 'iap' },
  { file: 'iap-linux-amd64', label: 'Linux · x86-64', saveAs: 'iap' },
  { file: 'iap-linux-arm64', label: 'Linux · ARM64', saveAs: 'iap' },
  { file: 'iap-windows-amd64.exe', label: 'Windows', saveAs: 'iap.exe', windows: true },
  { file: 'checksums-cli.txt', label: 'SHA-256 checksums', saveAs: 'checksums-cli.txt' },
];
const SSH_AGENT_FILES: ReleaseFile[] = [
  { file: 'ssh-agent-linux-amd64', label: 'Linux · x86-64', saveAs: 'ssh-agent-linux-amd64' },
  { file: 'ssh-agent-linux-arm64', label: 'Linux · ARM64', saveAs: 'ssh-agent-linux-arm64' },
  { file: 'checksums-agent.txt', label: 'SHA-256 checksums', saveAs: 'checksums-agent.txt' },
];
const RELAY_AGENT_FILES: ReleaseFile[] = [
  { file: 'relay-agent-windows-amd64.exe', label: 'Windows · RDP host', saveAs: 'relay-agent-windows-amd64.exe', windows: true },
  { file: 'install-relay-agent.ps1', label: 'Windows service installer', saveAs: 'install-relay-agent.ps1', windows: true },
  { file: 'relay-agent-linux-amd64', label: 'Linux · x86-64', saveAs: 'relay-agent-linux-amd64' },
  { file: 'relay-agent-linux-arm64', label: 'Linux · ARM64', saveAs: 'relay-agent-linux-arm64' },
  { file: 'checksums-relay-agent.txt', label: 'SHA-256 checksums', saveAs: 'checksums-relay-agent.txt' },
];

const OS_TABS: { key: OSKey; label: string }[] = [
  { key: 'mac-arm', label: 'macOS · Apple Silicon' },
  { key: 'mac-intel', label: 'macOS · Intel' },
  { key: 'linux', label: 'Linux' },
  { key: 'windows', label: 'Windows' },
];

//
// The chosen OS lives in the URL as `?os=` (design eval F24), shared by every
// OSTabs on the page: pick macOS once and each step shows its macOS command, and
// a link or refresh keeps it. A block that has no command for that OS shows its
// first one without changing the choice. This is useUrlState rather than Tabs'
// `urlParam` on purpose: urlParam writes each strip's own value back to the
// URL, so a Linux-only strip would overwrite the macOS choice for all the rest.
function OSTabs({ commands, lang = 'bash' }: { commands: Partial<Record<OSKey, string>>; lang?: string }) {
  const available = OS_TABS.filter((o) => commands[o.key] != null);
  const [url, setUrl] = useUrlState({ os: '' });
  const chosen = available.find((o) => o.key === url.os)?.key;
  const active: OSKey = chosen ?? available[0]?.key ?? 'linux';
  const setActive = (next: OSKey) => setUrl({ os: next }, { replace: true });
  const current = commands[active] ?? commands[available[0]?.key ?? 'linux'] ?? '';
  return (
    <div className="my-3 rounded-lg border border-[var(--d2d-border)] overflow-hidden">
      <Tabs
        value={active}
        onChange={(v) => setActive(v as OSKey)}
        aria-label="Operating system"
        className="bg-[var(--ds-color-bg-subtle)] px-2"
      >
        <Tabs.List className="flex-wrap">
          {available.map((o) => (
            <Tabs.Tab key={o.key} value={o.key}>
              {o.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      <CodeBlock code={current ?? ''} lang={lang} embedded />
    </div>
  );
}

// Step renders one item of a vertical stepper: a number badge with a hairline
// connector running down to the next step (hidden on the group's last step).
function Step({ n, title, children }: { n: number; title: string; children?: ReactNode }) {
  return (
    <div className="group/step relative flex gap-3">
      <span
        aria-hidden
        className="absolute left-[13px] top-8 bottom-1 w-px bg-[var(--d2d-border)] group-last/step:hidden"
      />
      <div className="relative flex-shrink-0 flex items-center justify-center h-7 w-7 rounded-full border border-[var(--d2d-border)] bg-[var(--accent-tag-bg)] text-[var(--accent-tag-text)] text-xs font-semibold">
        {n}
      </div>
      <div className="flex-1 min-w-0 pb-6 group-last/step:pb-0">
        <p className="pt-0.5 font-medium text-[var(--d2d-text-primary)]">{title}</p>
        {children && <div className="mt-1.5 text-sm text-[var(--d2d-text-secondary)]">{children}</div>}
      </div>
    </div>
  );
}

type CalloutKind = 'info' | 'warning' | 'tip';
function Callout({ kind = 'info', children }: { kind?: CalloutKind; children: React.ReactNode }) {
  const cfg = {
    info: { icon: Info, cls: 'border-[var(--zta-tint-info-border)] bg-[var(--zta-tint-info)] text-[var(--d2d-info)]' },
    warning: { icon: AlertTriangle, cls: 'border-[var(--zta-tint-warning-border)] bg-[var(--zta-tint-warning)] text-[var(--d2d-warning)]' },
    tip: { icon: Lightbulb, cls: 'border-[var(--zta-tint-success-border)] bg-[var(--zta-tint-success)] text-[var(--d2d-success)]' },
  }[kind];
  const Icon = cfg.icon;
  return (
    <div className={clsx('flex gap-2 rounded-lg border px-3 py-2.5 my-3 text-sm', cfg.cls)}>
      <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// UiPath renders a clickable-looking breadcrumb of the admin UI navigation.
// The snippets ship with placeholders and TLS verification on (design eval
// F16): a copied block must not carry a shared dev token or turn certificate
// checks off.
function InsecureDefaultsNote() {
  return (
    <Callout kind="warning">
      Replace <code>&lt;AGENT_TOKEN&gt;</code> with the token for this agent (<UiPath parts={['Settings', 'Agents']} />), and keep
      it out of shell history and version control. TLS to the hub is verified; <code>DEV_INSECURE_TLS=true</code> turns
      that off and is only for a local development hub with a self-signed certificate, never production.
    </Callout>
  );
}

function UiPath({ parts }: { parts: string[] }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1 align-middle">
      {parts.map((p, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <span className="rounded-md bg-[var(--ds-color-bg-subtle)] px-2 py-0.5 text-xs font-medium text-[var(--d2d-text-secondary)]">
            {p}
          </span>
          {i < parts.length - 1 && <ChevronRight className="h-3 w-3 text-[var(--d2d-text-muted)]" />}
        </span>
      ))}
    </span>
  );
}

// Figure renders a screenshot in a framed figure: the image sits on a quiet
// tinted mat inside a bordered frame, with the caption as the frame's footer.
// Falls back to a placeholder box if no image is supplied yet. The source PNGs
// are very high-resolution, so the preview is capped (height + width) and
// centered to keep the guide compact — click to open the full-size image in a
// new tab.
function Figure({ caption, src }: { caption: string; src?: string }) {
  return (
    <figure className="my-4 overflow-hidden rounded-lg border border-[var(--d2d-border)] bg-[var(--d2d-bg-card)]">
      <div className="bg-[var(--d2d-bg-secondary)] p-3">
        {src ? (
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            title="Open full-size screenshot"
            className="block group rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-ring)]"
          >
            <img
              src={src}
              alt={caption}
              loading="lazy"
              className="mx-auto max-h-[360px] w-auto max-w-full rounded-md border border-[var(--d2d-border)] bg-[var(--d2d-bg-card)] object-contain transition-shadow group-hover:shadow-[var(--shadow-overlay)]"
            />
          </a>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-md border-2 border-dashed border-[var(--d2d-border)] py-8 text-[var(--d2d-text-muted)]">
            <Monitor className="h-6 w-6 mb-1.5 opacity-60" />
            <span className="text-xs">Screenshot placeholder</span>
          </div>
        )}
      </div>
      <figcaption className="border-t border-[var(--d2d-border)] px-3 py-2 text-xs text-[var(--d2d-text-muted)]">{caption}</figcaption>
    </figure>
  );
}

// The troubleshooting table in "Add a Proxy (HTTP) Resource".
interface TroubleshootingRow {
  key: string;
  symptom: React.ReactNode;
  cause: React.ReactNode;
}

const TROUBLESHOOTING_COLUMNS: DataTableColumn<TroubleshootingRow>[] = [
  { key: 'symptom', header: 'Symptom', cell: (r) => r.symptom },
  { key: 'cause', header: 'Usual cause', cell: (r) => r.cause },
];

const TROUBLESHOOTING_ROWS: TroubleshootingRow[] = [
  {
    key: 'timeout',
    symptom: <code>Cross-instance request timeout</code>,
    cause: <>The hub has no connection for that <code>agent_id</code> — agent down, ID mismatch, or an ID collision.</>,
  },
  {
    key: 'reset',
    symptom: 'Connection closed / reset, no response',
    cause: 'No route matches the subdomain, so the request fell through to an unrelated upstream.',
  },
  {
    key: 'redirects',
    symptom: <code>ERR_TOO_MANY_REDIRECTS</code>,
    cause: <>The upstream is redirecting to its own address. Point at the UI port, and tell the app its public URL (for example MinIO's <code>MINIO_BROWSER_REDIRECT_URL</code>).</>,
  },
  {
    key: 'websocket',
    symptom: 'Page loads, live features hang',
    cause: <>A WebSocket the app needs. Tunnelling through an agent is supported — check the agent log for <code>ws: dial … failed</code>, which reports the upstream's own status code.</>,
  },
  {
    key: 'collision',
    symptom: 'Agent looks healthy, app unreachable',
    cause: 'Another agent took the ID. Probes cannot see this: they report whether a socket is held, not whether the hub routes to it.',
  },
];

interface SectionDef {
  id: string;
  title: string;
  icon: any;
}

const SECTIONS: SectionDef[] = [
  { id: 'overview', title: 'How it works', icon: ShieldCheck },
  { id: 'host-agent', title: 'Install the Host (SSH) Agent', icon: Server },
  { id: 'k8s-agent', title: 'Install the Kubernetes Agent', icon: Box },
  { id: 'databases', title: 'Add a Database', icon: Database },
  { id: 'desktop', title: 'Add a Desktop (RDP)', icon: Monitor },
  { id: 'relay-agent', title: 'Private RDP & DBs (Relay Agent)', icon: Network },
  { id: 'proxy', title: 'Add a Proxy (HTTP) Resource', icon: Globe },
  { id: 'cloud-providers', title: 'Add a Cloud Provider', icon: Cloud },
  { id: 'verify', title: 'Verify & Troubleshoot', icon: Terminal },
];

// A small Mist Violet Feature Icon Tile, used by section headers.
function IconTile({ icon: Icon }: { icon: any }) {
  return (
    <span
      aria-hidden
      className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-[var(--radius-icons)] bg-[var(--accent-tag-bg)] text-[var(--accent-tag-text)]"
    >
      <Icon className="h-[18px] w-[18px]" />
    </span>
  );
}

// One guide section as a card: a header with the section's icon tile and
// title, then the body. The `id` the table of contents links to lives on the
// <section> itself. Body children keep their own margins; the first and last
// are trimmed so every card has the same padding rhythm.
function GuideSection({ id, icon, title, children }: SectionDef & { children: ReactNode }) {
  const headingId = `${id}-heading`;
  return (
    <section id={id} aria-labelledby={headingId} className="scroll-mt-6">
      <Card>
        <Card.Header>
          <div className="flex items-center gap-3">
            <IconTile icon={icon} />
            <h2 id={headingId} className="text-lg font-medium text-[var(--d2d-text-primary)]">{title}</h2>
          </div>
        </Card.Header>
        <Card.Body className="[&>:first-child]:mt-0 [&>:last-child]:mb-0">{children}</Card.Body>
      </Card>
    </section>
  );
}

const TOC_ITEMS: SectionDef[] = [{ id: 'tours', title: 'Guided tours', icon: Compass }, ...SECTIONS];

// Tracks which section is in view inside the app's scroll area (#app-scroll),
// so the table of contents can highlight it. A section counts as current once
// its top has crossed the upper third of the viewport.
function useActiveSection(ids: string[]): [string, (id: string) => void] {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const root = document.getElementById('app-scroll');
    const visible = new Map<string, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) visible.set(e.target.id, e.isIntersecting);
        const first = ids.find((id) => visible.get(id));
        if (first && !atBottom()) setActive(first);
      },
      { root, rootMargin: '0px 0px -65% 0px', threshold: 0 }
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    // The last sections are too short to reach the top band, so at the end of
    // the page the last one is current.
    const atBottom = () => !!root && root.scrollTop + root.clientHeight >= root.scrollHeight - 4;
    const onScroll = () => { if (atBottom()) setActive(ids[ids.length - 1]); };
    root?.addEventListener('scroll', onScroll, { passive: true });
    return () => { observer.disconnect(); root?.removeEventListener('scroll', onScroll); };
  }, [ids]);
  return [active, setActive];
}

const TOC_IDS = TOC_ITEMS.map((s) => s.id);

function TableOfContents() {
  const [active, setActive] = useActiveSection(TOC_IDS);
  const go = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the page's own scroller: scrollIntoView also scrolls every
    // ancestor that can scroll, which moved the app shell.
    const scroller = document.getElementById('app-scroll');
    if (scroller) {
      // The guide's screenshots load lazily as the scroll passes them, so the
      // page grows mid-scroll and the target moves. Re-aim once each scroll
      // settles until the section sits at the top (a few passes at most).
      const offset = () => el.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 16;
      let passes = 0;
      const aim = (smooth: boolean) => scroller.scrollTo({ top: scroller.scrollTop + offset(), behavior: smooth ? 'smooth' : 'auto' });
      const settle = () => {
        if (Math.abs(offset()) > 4 && passes++ < 3) { aim(false); setTimeout(settle, 250); }
      };
      aim(!reduce);
      setTimeout(settle, reduce ? 150 : 900);
    } else {
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
    window.history.replaceState(null, '', `#${id}`);
    setActive(id);
  };
  return (
    <nav aria-label="On this page" className="hidden lg:block sticky top-6 self-start">
      <p className="text-label font-semibold uppercase text-[var(--d2d-text-muted)] mb-2 px-2">On this page</p>
      <ul className="space-y-0.5 border-l border-[var(--d2d-border)]">
        {TOC_ITEMS.map((s) => {
          const current = active === s.id;
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => go(e, s.id)}
                aria-current={current ? 'location' : undefined}
                className={clsx(
                  '-ml-px flex items-center gap-2 border-l-2 rounded-r-md px-2 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-ring)]',
                  current
                    ? 'border-[var(--d2d-accent)] bg-[var(--ds-color-bg-hover)] font-medium text-[var(--d2d-text-primary)]'
                    : 'border-transparent text-[var(--d2d-text-secondary)] hover:bg-[var(--ds-color-bg-hover)] hover:text-[var(--d2d-text-primary)]'
                )}
              >
                <s.icon
                  aria-hidden
                  className={clsx('h-4 w-4 flex-shrink-0', current ? 'text-[var(--d2d-accent)]' : 'text-[var(--d2d-text-muted)]')}
                />
                {s.title}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ─────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────── */

export default function SetupGuide() {
  // One CLI build serves every workspace; the user names theirs at sign-in.
  const tenant = getTenant() || '<workspace>';
  return (
    <div className="pb-16">
      {/* Header */}
      <PageHeader
        className="mb-6"
        icon={<Rocket />}
        title="Getting Started"
        description={`Install agents and register resources so users can reach them securely through ${BRAND.name}.`}
      />

      <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-8">
        {/* Table of contents — sticky inside #app-scroll, highlights the
            section in view; hidden below lg. */}
        <TableOfContents />

        {/* Content — min-w-0 lets this grid track shrink below its widest child
            (the code blocks) so they scroll inside their own box instead of
            widening the whole page. */}
        <div className="min-w-0 space-y-6">
          {/* Guided tours — feature-wise walkthroughs the user can start, pause, and
              resume later. Progress is remembered per user across visits. */}
          <section id="tours" aria-labelledby="tours-heading" className="scroll-mt-6">
            <div className="flex items-start gap-3 mb-4">
              <IconTile icon={Compass} />
              <div className="min-w-0 flex-1">
                <h2 id="tours-heading" className="text-lg font-medium text-[var(--d2d-text-primary)]">Guided tours</h2>
                <p className="text-sm text-[var(--d2d-text-secondary)] mt-0.5">
                  Take an interactive walkthrough of each feature. You can pause a tour at any point and
                  resume it later from here — your progress is saved.
                </p>
                <TourProgressSummary className="mt-3" />
              </div>
            </div>
            <TourList />
          </section>

          {/* Overview */}
          <GuideSection {...SECTIONS[0]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              {BRAND.name} brokers every connection between a user and a resource. Users never get direct
              network access — they authenticate to the gateway (SSO/OIDC), the gateway checks policy and access
              requests, and only then proxies the session. There are two ways a resource reaches the gateway:
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-lg border border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Box className="h-4 w-4 text-[var(--d2d-text-primary)]" />
                  <p className="font-medium text-[var(--d2d-text-primary)]">Agent-based (outbound)</p>
                </div>
                <p className="text-sm text-[var(--d2d-text-secondary)]">
                  An agent runs <em>next to</em> the resource (on a host or inside a cluster) and dials <strong>out</strong> to
                  the hub over a WebSocket. No inbound ports need opening. Used for SSH hosts, Kubernetes clusters, and
                  internal HTTP apps.
                </p>
              </div>
              <div className="rounded-lg border border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Globe className="h-4 w-4 text-[var(--d2d-text-primary)]" />
                  <p className="font-medium text-[var(--d2d-text-primary)]">Direct / credentialed</p>
                </div>
                <p className="text-sm text-[var(--d2d-text-secondary)]">
                  The gateway connects to the resource itself using stored credentials or a kubeconfig — used for managed
                  databases, cloud Kubernetes (EKS/AKS/GKE), and desktops the gateway can already reach.
                </p>
              </div>
            </div>
            <Callout kind="info">
              Every resource you register shows up under its section in the sidebar (Resources, K8s Resources, Desktop
              Access, Proxy Resources) with a live health/online status and a one-click connect action.
            </Callout>
          </GuideSection>

          {/* Host Agent */}
          <GuideSection {...SECTIONS[1]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              The host agent turns any Linux server into an SSH resource reachable through the gateway. It runs as a
              systemd service and connects outbound to the hub.
            </p>
            <div className="mt-4">
              <Step n={1} title="Get the binary — build it, or download it">
                <p className="mb-1">
                  <strong>Option A · Build manually</strong> from the <code>d2d-ZTA</code> repo (needs Go 1.24+):
                </p>
                <p className="mt-1 mb-0 text-sm text-[var(--d2d-text-muted)]">iap CLI — pick your workstation OS:</p>
                <OSTabs
                  commands={{
                    'mac-arm': `CGO_ENABLED=0 GOOS=darwin GOARCH=arm64 go build -o iap ./cmd/iap-cli`,
                    'mac-intel': `CGO_ENABLED=0 GOOS=darwin GOARCH=amd64 go build -o iap ./cmd/iap-cli`,
                    linux: `CGO_ENABLED=0 GOOS=linux GOARCH=amd64 go build -o iap ./cmd/iap-cli`,
                    windows: `CGO_ENABLED=0 GOOS=windows GOARCH=amd64 go build -o iap.exe ./cmd/iap-cli`,
                  }}
                />
                <p className="mt-2 mb-0 text-sm text-[var(--d2d-text-muted)]">
                  SSH host agent — always built for the Linux server:
                </p>
                <CodeBlock
                  code={`CGO_ENABLED=0 GOOS=linux GOARCH=amd64 go build -o ssh-agent-linux-amd64 ./cmd/ssh-agent
CGO_ENABLED=0 GOOS=linux GOARCH=arm64 go build -o ssh-agent-linux-arm64 ./cmd/ssh-agent`}
                />
                <p className="mt-3 mb-1">
                  <strong>Option B · Download a prebuilt binary</strong> (alternative to building). The release bucket
                  is private: create a link, valid for a few minutes, for the file you need.
                </p>
                <p className="mt-1 mb-0 text-sm text-[var(--d2d-text-muted)]">iap CLI — for your workstation:</p>
                <ReleaseDownload component="cli" files={CLI_FILES} />
                <p className="mt-1 mb-0 text-sm text-[var(--d2d-text-muted)]">
                  The same CLI serves every workspace. Sign in once with this one, and it is remembered:
                </p>
                <CodeBlock code={`iap --tenant ${tenant} auth login`} />
                <p className="mt-2 mb-0 text-sm text-[var(--d2d-text-muted)]">SSH agent — for the Linux server:</p>
                <ReleaseDownload component="ssh-agent" files={SSH_AGENT_FILES} />
              </Step>
              <Step n={2} title="Install the agent as a systemd service">
                Requires <code>sshd</code> running on the host. Edit <code>User</code>, <code>IAP_SERVER_ID</code>,{' '}
                <code>IAP_AGENT_TOKEN</code>, <code>IAP_HUB_URL</code>, <code>SSH_KEY_PATH</code> and{' '}
                <code>SSH_USERNAME</code> for your server, and point <code>ExecStart</code> at the binary from step 1:
                <CodeBlock
                  code={`# Create systemd service file
sudo tee /etc/systemd/system/iap-ssh-agent.service <<EOF
[Unit]
Description=${BRAND.name} SSH Agent
After=network.target ssh.service

[Service]
Type=simple
User=ec2-user
Environment=IAP_SERVER_ID=<server_name>
Environment=IAP_AGENT_TOKEN=<AGENT_TOKEN>
Environment=IAP_HUB_URL=wss://iap.example.com/agent/ws
Environment=SSH_KEY_PATH=/home/ec2-user/example.pem
Environment=SSH_USERNAME=<username of server>
# Uncomment for keyless CLI access (see step 5):
# Environment=SSH_CREDENTIAL_INJECTION=true
ExecStart=/home/ec2-user/ssh-agent-linux-amd64
Restart=always
RestartSec=10

[Install]
WantedBy=multi-user.target
EOF

sudo systemctl daemon-reload
sudo systemctl enable iap-ssh-agent
sudo systemctl start iap-ssh-agent`}
                />
                <InsecureDefaultsNote />
              </Step>
              <Step n={3} title="Check the service status & logs">
                <CodeBlock code={`sudo systemctl status iap-ssh-agent
journalctl -u iap-ssh-agent -f`} />
              </Step>
              <Step n={4} title="Verify in the UI">
                Open <UiPath parts={['SSH Resources', 'SSH Servers']} /> — the host appears with an{' '}
                <strong>Online</strong> status within a few seconds. By default, connect with the CLI and your own key:{' '}
                <code>iap ssh connect &lt;server&gt; --background</code>, then{' '}
                <code>ssh -p 2222 -o HostKeyAlias=&lt;server&gt; -i &lt;key.pem&gt; &lt;user&gt;@localhost</code>.
              </Step>
              <Step n={5} title="(Optional) Keyless CLI access — credential injection">
                To let users connect with <strong>no key on their machine</strong>, enable credential injection: the
                agent authenticates to the target on the user's behalf using its own <code>SSH_KEY_PATH</code> (the same
                model as the web terminal). This is an explicit, per-server opt-in that takes <strong>two one-time
                steps</strong>:
                <div className="mt-2">
                  <p className="mb-1"><strong>1. On the agent</strong> — enable injection in the systemd unit (uncomment the line from step 2), then restart:</p>
                  <CodeBlock code={`Environment=SSH_CREDENTIAL_INJECTION=true

sudo systemctl daemon-reload && sudo systemctl restart iap-ssh-agent`} />
                  <p className="mt-2 mb-1">
                    <strong>2. On the resource</strong> — under{' '}
                    <UiPath parts={['SSH Resources', 'SSH Servers']} /> (or <UiPath parts={['Resources']} />), edit the
                    server and add this to <strong>Labels</strong>:
                  </p>
                  <CodeBlock lang="json" code={`{ "credential_injection": "true" }`} />
                  <p className="mt-2 mb-1">Then connect with <strong>no <code>-i</code></strong>:</p>
                  <CodeBlock code={`ssh-keygen -R <server>     # once: host key is now the agent's, not the target's
iap ssh connect <server> --background
ssh -p 2222 -o HostKeyAlias=<server> <user>@localhost   # keyless`} />
                </div>
                <Callout kind="info">
                  <strong>Why both settings?</strong> They configure two different processes: the{' '}
                  <code>SSH_CREDENTIAL_INJECTION</code> env tells the <em>agent</em> to inject its key, and the{' '}
                  <code>credential_injection</code> label tells the <em>gateway</em> to route the tunnel through that
                  agent (instead of dialing the host directly). Both are required — set only one and it falls back to the
                  key-based path.
                </Callout>
              </Step>
            </div>
            <Figure src={imgSshServers} caption="Resources → SSH: the enrolled hosts showing Online" />
          </GuideSection>

          {/* K8s Agent */}
          <GuideSection {...SECTIONS[2]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              The Kubernetes agent runs <em>inside</em> the target cluster as a Deployment. It registers the cluster with
              the hub and proxies both <code>kubectl</code> web-terminal sessions and API access — no inbound access to
              the cluster is required.
            </p>
            <div className="mt-4">
              <Step n={1} title="Create the agent token">
                Under <UiPath parts={['K8s Resources', 'Add Cluster', 'Agent']} />, generate a token for the cluster
                (e.g. <code>prod-k8s</code>).
              </Step>
              <Step n={2} title="Apply the agent manifest">
                Save the manifest below as <code>k8s-agent-deployment.yaml</code>. It bundles a ServiceAccount, RBAC
                (read + <code>pods/exec</code>), the Deployment (with startup/readiness/liveness probes), and the token
                Secret. Change <code>K8S_CLUSTER_ID</code>, <code>IAP_AGENT_HUB_URL</code>, the image tag, and the Secret{' '}
                <code>token</code> for your environment:
                <CodeBlock
                  lang="yaml"
                  code={`# K8s Agent Deployment
# Runs inside your Kubernetes cluster and connects to the ${BRAND.name} agent hub
apiVersion: v1
kind: ServiceAccount
metadata:
  name: iap-k8s-agent
  namespace: default
---
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRole
metadata:
  name: iap-k8s-agent
rules:
  # Read-only access to most resources for proxying
  - apiGroups: ["*"]
    resources: ["*"]
    verbs: ["get", "list", "watch"]
  # Pod exec for kubectl exec functionality
  - apiGroups: [""]
    resources: ["pods/exec", "pods/log", "pods/attach"]
    verbs: ["get", "create"]
---
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRoleBinding
metadata:
  name: iap-k8s-agent
subjects:
  - kind: ServiceAccount
    name: iap-k8s-agent
    namespace: default
roleRef:
  kind: ClusterRole
  name: iap-k8s-agent
  apiGroup: rbac.authorization.k8s.io
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: iap-k8s-agent
  namespace: default
  labels:
    app: iap-k8s-agent
spec:
  replicas: 1
  selector:
    matchLabels:
      app: iap-k8s-agent
  template:
    metadata:
      labels:
        app: iap-k8s-agent
    spec:
      serviceAccountName: iap-k8s-agent
      imagePullSecrets:
        - name: acr-secret
      containers:
        - name: k8s-agent
          image: 267048487300.dkr.ecr.ap-south-1.amazonaws.com/iap-k8s-agent:iap-ft-78
          imagePullPolicy: Always
          env:
            - name: K8S_CLUSTER_ID
              value: "change me the cluster name"
            - name: IAP_AGENT_TOKEN
              valueFrom:
                secretKeyRef:
                  name: iap-agent-secret
                  key: token
            - name: IAP_AGENT_HUB_URL
              value: "wss://iap.example.com"
            - name: NAMESPACE
              valueFrom:
                fieldRef:
                  fieldPath: metadata.namespace
          resources:
            limits:
              cpu: 100m
              memory: 128Mi
            requests:
              cpu: 50m
              memory: 64Mi
          # /tmp/healthy exists only while the agent holds a live hub connection
          # (created on connect, removed on disconnect by the agent itself).
          #
          # startupProbe: gives the first connect up to ~2min (24 x 5s) before the
          # liveness clock starts, so a slow hub/network at boot never gets the pod killed.
          startupProbe:
            exec:
              command: ["cat", "/tmp/healthy"]
            periodSeconds: 5
            failureThreshold: 24
            timeoutSeconds: 2
          # readinessProbe: reflects "connected right now". Flips the pod NotReady
          # quickly (~20s) when the hub link drops; recovers as soon as it reconnects.
          readinessProbe:
            exec:
              command: ["cat", "/tmp/healthy"]
            periodSeconds: 10
            failureThreshold: 2
            timeoutSeconds: 2
          # livenessProbe: restarts the pod only if it stays disconnected for a
          # sustained period (~60s), i.e. the agent is wedged and can't self-recover.
          # Deliberately more tolerant than readiness to avoid churn on brief blips.
          livenessProbe:
            exec:
              command: ["cat", "/tmp/healthy"]
            periodSeconds: 10
            failureThreshold: 6
            timeoutSeconds: 2
---
apiVersion: v1
kind: Secret
metadata:
  name: iap-agent-secret
  namespace: default
type: Opaque
stringData:
  # Plain text token - no base64 encoding needed with stringData
  token: "<AGENT_TOKEN>"`}
                />
                <CodeBlock code={`kubectl apply -f k8s-agent-deployment.yaml`} />
                <InsecureDefaultsNote />
              </Step>
              <Step n={3} title="Check the pod is Ready">
                <CodeBlock code={`kubectl get pod -l app=iap-k8s-agent
# READY 1/1, STATUS Running`} />
              </Step>
              <Step n={4} title="Connect">
                In <UiPath parts={['K8s Resources']} /> the cluster shows <strong>Online · Agent</strong>. Click{' '}
                <strong>Terminal</strong> for a browser <code>kubectl</code> shell, or use the CLI:{' '}
                <code>iap k8s connect &lt;cluster&gt;</code>.
              </Step>
            </div>
            <Callout kind="tip">
              The agent self-registers on connect and maintains a heartbeat. If it ever loses the hub link it reconnects
              automatically; its liveness probe restarts the pod if it can't recover.
            </Callout>
            <p className="mt-4 text-sm text-[var(--d2d-text-secondary)]">
              For a cloud cluster reached via kubeconfig instead of an agent (e.g. EKS), use{' '}
              <UiPath parts={['K8s Resources', 'Add Cluster']} /> and paste the kubeconfig — the CLI then tunnels to its
              API through the gateway.
            </p>
            <Figure src={imgK8sAddCluster} caption="Add Cluster: register a kubeconfig-based cluster (e.g. EKS)" />
            <Figure src={imgK8sResources} caption="K8s Resources: agent cluster Online (Terminal), kubeconfig cluster (CLI)" />

            <h3 className="mt-6 font-medium text-[var(--d2d-text-primary)]">
              EKS only · grant your IAM identity access to the cluster (required per user)
            </h3>
            <p className="mt-2 text-sm text-[var(--d2d-text-secondary)]">
              For EKS clusters the gateway tunnels your <code>kubectl</code> traffic straight to the EKS API, and the API
              authenticates <em>you</em> using your own AWS identity (<code>aws eks get-token</code>). EKS has{' '}
              <strong>two separate authorization layers</strong> — AWS IAM controls who can call the AWS API, while
              Kubernetes RBAC controls what you can do <em>inside</em> the cluster. Having AWS{' '}
              <code>AdministratorAccess</code> only satisfies the first; your IAM principal must <em>also</em> be mapped
              into the cluster or the connection fails with <code>401 · the server has asked for the client to provide
              credentials</code>.
            </p>
            <Callout kind="warning">
              Only the IAM principal that <strong>created</strong> the cluster gets access automatically. Every other
              user — even IAM admins — must be added once via an EKS access entry (below) before{' '}
              <code>iap k8s connect</code> will work for them.
            </Callout>
            <div className="mt-4">
              <Step n={1} title="Find your IAM identity">
                Run this with the same AWS credentials your CLI uses, and copy the <code>Arn</code>:
                <CodeBlock code={`aws sts get-caller-identity
# "Arn": "arn:aws:iam::<account-id>:user/you@example.com"`} />
              </Step>
              <Step n={2} title="Create the access entry">
                Map that ARN into the cluster (run by anyone with EKS admin IAM — needs no cluster access itself):
                <CodeBlock code={`aws eks create-access-entry \\
  --cluster-name <cluster-name> --region <region> \\
  --principal-arn <your-arn>`} />
              </Step>
              <Step n={3} title="Associate an access policy">
                Grant permissions inside the cluster. Cluster-wide admin:
                <CodeBlock code={`aws eks associate-access-policy \\
  --cluster-name <cluster-name> --region <region> \\
  --principal-arn <your-arn> \\
  --access-scope type=cluster \\
  --policy-arn arn:aws:eks::aws:cluster-access-policy/AmazonEKSClusterAdminPolicy`} />
                For least-privilege instead, use <code>AmazonEKSViewPolicy</code> or <code>AmazonEKSEditPolicy</code>, and
                scope to specific namespaces with <code>--access-scope type=namespace,namespaces=ns1,ns2</code>.
              </Step>
              <Step n={4} title="Connect">
                Access entries apply within seconds — no wait needed:
                <CodeBlock code={`iap k8s connect <cluster>
kubectl --kubeconfig ~/.iap/k8s/<cluster>.yaml get pods`} />
              </Step>
            </div>
            <Callout kind="tip">
              Prefer <strong>EKS access entries</strong> (shown here) over editing the legacy <code>aws-auth</code>{' '}
              ConfigMap — they're managed entirely through the AWS API and don't require you to already have cluster
              access. This step is per IAM principal, so each new user needs it once.
            </Callout>
          </GuideSection>

          {/* Databases */}
          <GuideSection {...SECTIONS[3]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              Databases are registered as resources the gateway connects to with stored credentials. Users then get a
              short-lived local tunnel via the CLI (<code>iap db connect</code>) instead of the real credentials.
            </p>
            <div className="mt-4">
              <Step n={1} title="Open the database form">
                Go to <UiPath parts={['Resources', 'Add Resource', 'Database']} />.
              </Step>
              <Step n={2} title="Enter connection details">
                Engine (PostgreSQL / MySQL), host, port, database name, and the service credentials the gateway should
                use. For a DB inside a cluster or private network, point it at the in-cluster Service DNS (e.g.{' '}
                <code>my-db.namespace.svc:5432</code>) — the gateway reaches it through the cluster agent.
              </Step>
              <Step n={3} title="Test & save">
                Use <strong>Test Connection</strong> to validate, then save. Bind it to the roles that should have access
                under <UiPath parts={['Roles']} />.
              </Step>
              <Step n={4} title="Users connect">
                <CodeBlock code={`iap db connect <database-name>
# → local tunnel, e.g. localhost:5432, no real credentials exposed`} />
              </Step>
            </div>
            <Figure src={imgDatabaseAdd} caption="Add Database: engine, host/port, database name, credentials, SSL mode, and Test Connection" />
          </GuideSection>

          {/* Desktop */}
          <GuideSection {...SECTIONS[4]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              Desktop Access publishes Windows/Linux desktops over RDP, streamed to the browser through the gateway — no
              RDP client and no direct network route from the user.
            </p>
            <div className="mt-4">
              <Step n={1} title="Open the desktop form">
                Go to <UiPath parts={['Desktop Access', 'Add Desktop']} />.
              </Step>
              <Step n={2} title="Enter the target">
                Host/IP and RDP port (default <code>3389</code>), a display name, and the credentials or credential-broker
                settings. If the desktop is only reachable inside a cluster/VPC, ensure an agent in that network can reach
                it.
              </Step>
              <Step n={3} title="Grant access & launch">
                Bind to the appropriate roles, then users click <strong>Connect</strong> on{' '}
                <UiPath parts={['Desktop Access']} /> for an in-browser session (fully recorded).
              </Step>
            </div>
            <Figure src={imgDesktopAdd} caption="Add Desktop: host/port, credentials, screen size, and session controls" />
            <Figure src={imgDesktopConnect} caption="Desktop Access: the registered desktop with a Connect via RDP action" />
          </GuideSection>

          {/* Relay Agent — private RDP & databases (outbound) */}
          <GuideSection {...SECTIONS[5]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              For a Windows desktop or a database behind a <strong>private IP</strong> (another cloud, a VNet/VPC, or
              on-prem), run the <strong>relay agent</strong> near it. It connects <strong>outbound</strong> to the gateway
              and relays the connection — no public IP, no inbound firewall rule, no VPN. One binary handles both RDP and
              any database; it's the desktop/DB counterpart to the SSH and Kubernetes agents.
            </p>

            <Callout kind="info">
              The rule for every relayed resource: set its <strong>Host</strong> to the literal <code>agent-managed</code>,
              and make the resource <strong>name / external_id</strong> equal the agent's <code>IAP_SERVER_ID</code>. The
              real target address lives only in the agent's <code>RELAY_TARGET_ADDR</code>, never in the resource.
            </Callout>

            <div className="mt-4">
              <Step n={1} title="Get the relay-agent binary">
                Download it on the host near the target — <strong>Windows</strong> for RDP hosts (the binary and{' '}
                <code>install-relay-agent.ps1</code>), <strong>Linux</strong> for databases / TCP services:
                <ReleaseDownload component="relay-agent" files={RELAY_AGENT_FILES} />
              </Step>
            </div>

            <h3 className="mt-4 font-medium text-[var(--d2d-text-primary)]">A · Windows desktop (RDP)</h3>
            <p className="mt-1 text-sm text-[var(--d2d-text-secondary)]">
              Copy <code>relay-agent-windows-amd64.exe</code> and <code>install-relay-agent.ps1</code> onto the Windows
              server, then from an <strong>elevated PowerShell</strong> in that folder:
            </p>
            <CodeBlock lang="powershell" code={`.\\install-relay-agent.ps1 -ServerId win-dc01 -AgentToken '<agent-token>' -HubUrl 'wss://<gateway>/agent/ws'
# installs the "iap-relay-agent" Windows service; target defaults to 127.0.0.1:3389 (RDP on this host)`} />
            <p className="mt-1 text-sm text-[var(--d2d-text-secondary)]">
              Then add the desktop under <UiPath parts={['Desktop Access', 'Add Desktop']} /> with Host{' '}
              <code>agent-managed</code>, port <code>3389</code>, name <code>win-dc01</code>, and the RDP credentials.
            </p>

            <h3 className="mt-5 font-medium text-[var(--d2d-text-primary)]">B · Private database</h3>
            <p className="mt-1 text-sm text-[var(--d2d-text-secondary)]">
              Run the relay agent wherever it can reach the database, using <strong>either</strong> of the two options
              below — the PAM resource you register afterwards is identical. One agent instance serves one database (a
              unique <code>IAP_SERVER_ID</code> each).
            </p>

            <p className="mt-3 text-sm font-semibold text-[var(--d2d-text-primary)]">Option 1 — on a VM (systemd)</p>
            <p className="mt-1 text-sm text-[var(--d2d-text-secondary)]">
              Best for a managed cloud DB reachable from a VM (Azure Flexible Server, RDS). Run the Linux binary as a
              service:
            </p>
            <CodeBlock code={`sudo tee /etc/systemd/system/iap-relay-agent.service >/dev/null <<'EOF'
[Unit]
Description=${BRAND.name} Relay Agent (database)
After=network.target

[Service]
Environment=IAP_SERVER_ID=postgres-rds
Environment=IAP_AGENT_TOKEN=<agent-token>
Environment=IAP_HUB_URL=wss://<gateway>/agent/ws
Environment=RELAY_TARGET_ADDR=my-db.postgres.database.azure.com:5432
Environment=RELAY_RESOURCE_TYPE=database
ExecStart=/opt/iap/relay-agent-linux-amd64
Restart=always
RestartSec=5

[Install]
WantedBy=multi-user.target
EOF
sudo systemctl daemon-reload && sudo systemctl enable --now iap-relay-agent`} />

            <p className="mt-4 text-sm font-semibold text-[var(--d2d-text-primary)]">Option 2 — inside a Kubernetes cluster</p>
            <p className="mt-1 text-sm text-[var(--d2d-text-secondary)]">
              Best when the DB (or any service) lives in a cluster — the pod reaches it over cluster DNS and relays
              outbound. No <code>ServiceAccount</code>/RBAC is needed (it's a pure TCP relay). Save as{' '}
              <code>relay-agent.yaml</code> and <code>kubectl apply</code> it — the same manifest ships in the repo at{' '}
              <code>deploy/relay-agent/deployment.yaml</code>:
            </p>
            <CodeBlock lang="yaml" code={`apiVersion: v1
kind: Secret
metadata:
  name: iap-relay-agent-secret
  namespace: default
type: Opaque
stringData:
  token: "<agent-token>"
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: iap-relay-agent
  namespace: default
spec:
  replicas: 1
  selector:
    matchLabels: { app: iap-relay-agent }
  template:
    metadata:
      labels: { app: iap-relay-agent }
    spec:
      imagePullSecrets:
        - name: acr-secret
      containers:
        - name: relay-agent
          image: <registry>/iap-relay-agent:<tag>   # from the build-images workflow
          imagePullPolicy: Always
          env:
            - name: IAP_SERVER_ID            # == resource external_id in the UI
              value: "cluster-postgres"
            - name: IAP_AGENT_TOKEN
              valueFrom:
                secretKeyRef: { name: iap-relay-agent-secret, key: token }
            - name: IAP_HUB_URL
              value: "wss://<gateway>/agent/ws"
            - name: RELAY_TARGET_ADDR        # in-cluster Service DNS
              value: "postgres.default.svc.cluster.local:5432"
            - name: RELAY_RESOURCE_TYPE
              value: "database"
          resources:
            limits: { cpu: 100m, memory: 128Mi }
            requests: { cpu: 50m, memory: 64Mi }
          # /tmp/healthy exists only while the agent holds a live hub connection
          # (created on connect, removed on disconnect by the agent itself).
          startupProbe:                       # up to ~2min for first connect
            exec: { command: ["cat", "/tmp/healthy"] }
            periodSeconds: 5
            failureThreshold: 24
            timeoutSeconds: 2
          readinessProbe:                     # NotReady ~20s after the hub link drops
            exec: { command: ["cat", "/tmp/healthy"] }
            periodSeconds: 10
            failureThreshold: 2
            timeoutSeconds: 2
          livenessProbe:                      # restart only if wedged ~60s+
            exec: { command: ["cat", "/tmp/healthy"] }
            periodSeconds: 10
            failureThreshold: 6
            timeoutSeconds: 2`} />
            <CodeBlock code={`kubectl apply -f relay-agent.yaml
kubectl logs -l app=iap-relay-agent -f     # expect: Connected to hub ...`} />

            <p className="mt-3 text-sm text-[var(--d2d-text-secondary)]">
              Then add it under <UiPath parts={['Resources', 'Add Resource', 'Database']} /> with Host{' '}
              <code>agent-managed</code>, the DB port, <strong>SSL mode Require</strong> (managed DBs require SSL), name{' '}
              <code>postgres-rds</code>, and the DB credentials. Users connect normally:{' '}
              <code>iap db connect postgres-rds</code>.
            </p>

            <Callout kind="warning">
              <strong>“Test Connection” will fail</strong> for an <code>agent-managed</code> resource — that button dials
              the host directly and can't resolve the keyword. Ignore it and just save; validate with{' '}
              <code>iap db connect</code> (or by opening the desktop).
            </Callout>

            <Callout kind="tip">
              One agent instance relays to <strong>one</strong> target. For several databases on the same VM, create{' '}
              <strong>one service per DB</strong> (e.g. <code>iap-relay-mysql</code>, <code>iap-relay-psql2</code>), each
              with its own <code>IAP_SERVER_ID</code> and <code>RELAY_TARGET_ADDR</code>. Reuse the same binary, token, and
              hub URL.
            </Callout>
          </GuideSection>

          {/* Proxy Resources */}
          <GuideSection {...SECTIONS[6]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              Proxy Resources publish internal HTTP(S) apps at a gateway URL, with SSO enforced in front. Two setups
              depending on where the app lives:
            </p>

            <h3 className="mt-4 font-medium text-[var(--d2d-text-primary)]">A · App inside a Kubernetes cluster</h3>
            <div className="mt-2">
              <Step n={1} title="Deploy the HTTP Proxy Agent">
                The agent runs inside the cluster and dials <strong>out</strong> to the hub over a WebSocket (port
                8083) — no inbound ports need opening. Each entry in <code>HTTP_SERVICES</code> (a subdomain → in-cluster
                Service URL) is published as a Proxy Resource. Set <code>IAP_AGENT_ID</code>,{' '}
                <code>IAP_AGENT_HUB_URL</code>, the <code>agent-token</code> Secret, and your service list, then apply
                it. Like the K8s agent, it carries startup/readiness/liveness probes (via <code>GET /health</code>, which
                returns 200 only while the hub link is live) so a dropped connection is detected and the pod recovers on
                its own:
                <CodeBlock
                  lang="yaml"
                  code={`# HTTP Proxy Agent - Cluster Deployment
# Runs inside the cluster, dials OUT to the hub over a WebSocket (port 8083),
# and proxies each HTTP service listed in HTTP_SERVICES. No inbound ports.
apiVersion: v1
kind: Namespace
metadata:
  name: iap-system
  labels:
    app.kubernetes.io/part-of: iap
---
apiVersion: v1
kind: Secret
metadata:
  name: unified-agent-secret
  namespace: iap-system
  labels:
    app: unified-tunnel-agent
type: Opaque
stringData:
  agent-token: "<agent-token>"
---
apiVersion: v1
kind: ServiceAccount
metadata:
  name: unified-tunnel-agent
  namespace: iap-system
  labels:
    app: unified-tunnel-agent
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: unified-tunnel-agent
  namespace: iap-system
  labels:
    app: unified-tunnel-agent
    app.kubernetes.io/name: unified-tunnel-agent
    app.kubernetes.io/component: http-agent
spec:
  replicas: 1
  selector:
    matchLabels:
      app: unified-tunnel-agent
  template:
    metadata:
      labels:
        app: unified-tunnel-agent
    spec:
      serviceAccountName: unified-tunnel-agent
      containers:
      - name: unified-tunnel-agent
        image: 267048487300.dkr.ecr.ap-south-1.amazonaws.com/iap-http-agent:latest
        imagePullPolicy: Always
        ports:
        - name: health
          containerPort: 8080          # the agent's /health server (probes hit this)
        env:
        - name: IAP_AGENT_ID
          value: "k8s-ds-http-proxy-agent"
        # Hub WebSocket endpoint (agent-hub, port 8083)
        - name: IAP_AGENT_HUB_URL
          value: "ws://<hub_ip>:8083/agent/ws/k8s-ds-http-proxy-agent"
        - name: IAP_AGENT_TOKEN
          valueFrom:
            secretKeyRef:
              name: unified-agent-secret
              key: agent-token
        # Each entry becomes a Proxy Resource (subdomain -> in-cluster Service URL)
        - name: HTTP_SERVICES
          value: |
            [
              {"name":"argocd","url":"http://argocd-server.argocd.svc.cluster.local:80","subdomain":"argocd"},
              {"name":"vaultd","url":"http://vaultd-fe.vaultd.svc.cluster.local:80","subdomain":"vaultd"},
              {"name":"rabbitmq","url":"http://rabbitmq-rabbitmq.rabbitmq.svc.cluster.local:15672","subdomain":"rabbitmq"},
              {"name":"qualitygate","url":"http://sonarqube-sonarqube.sonarqube.svc.cluster.local:9000","subdomain":"qualitygate"},
              {"name":"godesk","url":"http://workdesk-frontend.workdesk-prod.svc.cluster.local:3000","subdomain":"godesk"},
              {"name":"hrms","url":"http://hrms-frontend.hrms-prod.svc.cluster.local:3000","subdomain":"hrms"}
            ]
        resources:
          limits:
            cpu: "200m"
            memory: "256Mi"
          requests:
            cpu: "50m"
            memory: "64Mi"
        # /health returns 200 only while the agent holds a live hub connection
        # (200 = connected, 503 = disconnected), so the probes below reflect real
        # connectivity. Uses httpGet (not a /tmp file) because the root FS is read-only.
        # startupProbe: give the first connect up to ~2min (24 x 5s) before liveness starts.
        startupProbe:
          httpGet:
            path: /health
            port: 8080
          periodSeconds: 5
          failureThreshold: 24
          timeoutSeconds: 2
        # readinessProbe: flips the pod NotReady quickly (~20s) when the hub link drops.
        readinessProbe:
          httpGet:
            path: /health
            port: 8080
          periodSeconds: 10
          failureThreshold: 2
          timeoutSeconds: 2
        # livenessProbe: restarts the pod only if it stays disconnected ~60s (wedged).
        livenessProbe:
          httpGet:
            path: /health
            port: 8080
          periodSeconds: 10
          failureThreshold: 6
          timeoutSeconds: 2
        securityContext:
          readOnlyRootFilesystem: true
          runAsNonRoot: true
          runAsUser: 1000
          allowPrivilegeEscalation: false`}
                />
                <CodeBlock code={`kubectl apply -f http-agent-deployment.yaml`} />
              </Step>
              <Step n={2} title="Confirm registration">
                Each service in <code>HTTP_SERVICES</code> appears under <UiPath parts={['Proxy Resources']} />. Adjust
                subdomain / path prefix / roles as needed. Check the pod is Ready with{' '}
                <code>kubectl get pod -n iap-system</code> (READY 1/1 = the hub link is live).
              </Step>
            </div>

            <h3 className="mt-5 font-medium text-[var(--d2d-text-primary)]">B · App on a server (manual)</h3>
            <div className="mt-2">
              <Step n={1} title="Add the proxy resource">
                Go to <UiPath parts={['Proxy Resources', 'Add Proxy Resource']} />.
              </Step>
              <Step n={2} title="Define routing">
                Upstream URL (where the app actually runs), the public subdomain/path to expose, and the auth mode. If the
                upstream is private, route it through a host or cluster agent that can reach it.
              </Step>
              <Step n={3} title="Bind roles & test">
                Attach the roles allowed to use it, save, then open the published URL — you'll be sent through SSO first.
              </Step>
            </div>

            <h3 className="mt-5 font-medium text-[var(--d2d-text-primary)]">C · App on a standalone server (Docker HTTP agent)</h3>
            <p className="mt-2 text-sm text-[var(--d2d-text-secondary)]">
              For an app on a plain server (e.g. Jenkins/MinIO on a VM), run the same <strong>HTTP agent</strong> from
              section A as a Docker container next to it. It dials <strong>out</strong> to the hub over a WebSocket
              (port 8083) — no inbound ports — and publishes each entry in <code>HTTP_SERVICES</code> as a Proxy
              Resource. Point each service's <code>url</code> at the app on the host (via{' '}
              <code>host.docker.internal</code>) and set <code>subdomain</code> to the public name. The agent
              auto-reconnects (keepalive + backoff) and <code>restart: unless-stopped</code> recovers the container.
            </p>
            <Callout kind="warning">
              Use the HTTP agent's own variables — <code>IAP_AGENT_ID</code>, <code>IAP_AGENT_HUB_URL</code>,{' '}
              <code>IAP_AGENT_TOKEN</code>, <code>HTTP_SERVICES</code> (with <code>url</code> + <code>subdomain</code>).
              Do <strong>not</strong> reuse the reverse-tunnel agent's <code>AGENT_TOKEN</code> /{' '}
              <code>TUNNEL_SERVER_ADDR</code> / <code>SERVICES</code> (<code>local_addr</code>) names — the HTTP agent
              ignores them and silently falls back to its default service.
            </Callout>
            <CodeBlock
              lang="yaml"
              code={`# ${BRAND.name} HTTP Agent - standalone server deployment
# Dials OUT to the hub over a WebSocket and publishes each local app as a Proxy
# Resource (no inbound ports on the server).
#   docker compose up -d
services:
  iap-http-agent:
    image: 267048487300.dkr.ecr.ap-south-1.amazonaws.com/iap-http-agent:latest
    container_name: iap-http-agent
    restart: unless-stopped          # recover the container if it ever exits
    environment:
      # Must match the agent_id in the route config on the ${BRAND.name} server
      IAP_AGENT_ID: "server-http-proxy-agent"
      # Hub WebSocket endpoint (agent-hub, port 8083)
      IAP_AGENT_HUB_URL: "ws://<hub-ip>:8083/agent/ws/server-http-proxy-agent"
      IAP_AGENT_TOKEN: "<agent-token>"
      # Each entry: url = app on this host (its ACTUAL port), subdomain = public name.
      # The app must listen on 0.0.0.0 (not 127.0.0.1) so the container's bridge IP
      # can reach it — verify with: curl http://172.17.0.1:<port>/ from the host.
      HTTP_SERVICES: |
        [
          {"name":"jenkins","url":"http://host.docker.internal:8000","subdomain":"ci"},
          {"name":"minio","url":"http://host.docker.internal:9000","subdomain":"minio"}
        ]
    extra_hosts:
      - "host.docker.internal:host-gateway"   # reach apps on the host
    deploy:
      resources:
        limits:   { cpus: "0.20", memory: 256M }
        reservations: { cpus: "0.05", memory: 64M }
    security_opt:
      - no-new-privileges:true
    read_only: true
    user: "1000:1000"`}
            />
            <Callout kind="tip">
              The agent self-heals: a 30s WebSocket ping + read deadline detect a dropped or half-open connection and it
              reconnects automatically (5s backoff). Confirm with <code>docker logs -f</code> — you want{' '}
              <code>initialized with N services</code> listing your apps, then{' '}
              <code>WebSocket connected to Agent Hub</code>. Each service appears under{' '}
              <UiPath parts={['Proxy Resources']} /> to bind roles and publish.
            </Callout>
            <Figure src={imgProxyEdit} caption="Proxy Resources: an HTTP proxy resource (upstream, subdomain, path prefix, priority)" />

            {/* routing.yaml — the part that is easy to get subtly wrong */}
            <h3 className="mt-8 mb-2 text-base font-medium text-[var(--d2d-text-primary)]">
              Routing rules (<code>config/http-proxy/routing.yaml</code>)
            </h3>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              The file has two halves and they do different jobs. <code>upstreams</code> only gives an address a name.
              Nothing is published until a <code>route</code> points at that name — an upstream on its own is inert, and
              a request that matches no route is sent to whichever upstream happens to be first, which fails in a way
              that looks like the app is down.
            </p>
            <CodeBlock
              lang="yaml"
              code={`upstreams:
  agent-hub:       "http://agent-hub:8080"      # required for any agent-based route
  observability-vm: "http://10.30.25.4:3000"    # a VM this proxy can reach directly

routes:
  # Direct: the proxy can reach the app itself (same VNet/VPC, peered, or local).
  - name: observability
    upstream: observability-vm
    subdomain: traced          # serves https://traced.<your-domain>
    strip_auth: false
    priority: 100
    # no agent_id — this is what makes it direct

  # Agent: the app is somewhere the proxy CANNOT reach (a cluster, a private host).
  - name: argocd
    upstream: agent-hub
    subdomain: cd
    agent_id: k8s-http-proxy-agent   # must match the agent's ID exactly
    strip_auth: false
    priority: 100`}
            />
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              Which style to use is decided by one question: can the http-proxy container open a TCP connection to the
              app? If yes, point an upstream straight at it. If no — it is inside a cluster, or bound to a private
              interface — run an agent beside the app and route through <code>agent-hub</code>.
            </p>
            <Callout kind="warning">
              <strong>Leaving <code>agent_id</code> on a route keeps it agent-based</strong> no matter which upstream you
              name. Changing the upstream to a VM address without removing <code>agent_id</code> looks like a direct
              route and behaves like an agent one.
            </Callout>
            <Callout kind="info">
              <code>subdomain</code> is matched as the first label of the host, exactly. <code>traced.example.com</code>{' '}
              needs <code>subdomain: traced</code>; hyphens are fine (<code>d2d-uem</code>). Two routes claiming the same
              subdomain is not an error — the first by priority wins and the other silently never serves.{' '}
              <code>routing.yaml</code> is read by <strong>http-proxy</strong>, so restart that service after editing it.
            </Callout>

            {/* Agent identity — the failure that is hardest to recognise */}
            <h3 className="mt-8 mb-2 text-base font-medium text-[var(--d2d-text-primary)]">
              Agent IDs must be unique, and must match in two places
            </h3>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              The hub identifies an agent by the <strong>last path segment of its hub URL</strong>, not by{' '}
              <code>IAP_AGENT_ID</code>. Both must agree, and no two agents may share an ID.
            </p>
            <CodeBlock
              lang="yaml"
              code={`IAP_AGENT_ID:      "k8s-prod-http-proxy-agent"
IAP_AGENT_HUB_URL: "wss://zta.example.com/agent/ws/k8s-prod-http-proxy-agent"
#                                                    ^ this is what the hub registers`}
            />
            <Callout kind="warning">
              Copying an agent manifest to a second cluster without changing <strong>both</strong> values is the most
              damaging mistake available here. An ID maps to one connection: the newcomer takes over, and the services
              behind the original stop being reachable — while both agents keep logging healthy heartbeats and their
              readiness probes stay green, because each still holds a live socket. The hub logs{' '}
              <code>Agent ID collision</code> with both source addresses; grep for it before assuming the app is at
              fault. Keep the agent Deployment at <code>replicas: 1</code> for the same reason — two pods collide with
              each other.
            </Callout>

            {/* Diagnosis */}
            <h3 className="mt-8 mb-2 text-base font-medium text-[var(--d2d-text-primary)]">
              When a published app will not load
            </h3>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              Work outwards from the hub — it names the agent it is looking for and lists the agents it currently holds,
              which settles routing questions immediately.
            </p>
            <CodeBlock
              code={`# 1. Does the hub have the agent the route asks for?
docker logs <agent-hub-container> 2>&1 | grep "Looking for agentID" | tail -3

# 2. Is anything else claiming that ID? (both source addresses are logged)
docker logs <agent-hub-container> 2>&1 | grep -E "Agent ID collision|for serverID" | tail -10

# 3. What does the agent itself think it is publishing?
#    "initialized with N services" must list the app, and the ID must be the expected one.
docker logs <agent-container> --tail 30        # or: kubectl logs deploy/<agent> -n iap-system`}
            />
            <Callout kind="tip">
              If a command prints nothing at all, check it ran before trusting the silence — piping stderr into{' '}
              <code>grep</code> hides <em>no such container</em> exactly like a genuine no-match. Run the bare command
              first. The same applies to <code>kubectl</code> when several clusters share a deployment name: confirm{' '}
              <code>kubectl config current-context</code> before concluding a restart had no effect.
            </Callout>
            <div className="my-3">
              <DataTable
                aria-label="Troubleshooting: symptom and usual cause"
                columns={TROUBLESHOOTING_COLUMNS}
                rows={TROUBLESHOOTING_ROWS}
                rowKey={(r) => r.key}
              />
            </div>
          </GuideSection>

          {/* Cloud Providers */}
          <GuideSection {...SECTIONS[7]}>
            <p className="text-sm text-[var(--d2d-text-secondary)]">
              Connect a cloud account so {BRAND.name} can broker identity-based access to its console and resources (e.g. the AWS
              console, EC2, S3) — users request access through roles instead of holding long-lived cloud credentials.
            </p>
            <div className="mt-4">
              <Step n={1} title="Open Cloud Providers">
                Go to <UiPath parts={['Cloud Providers']} /> and pick a provider (AWS, Azure, Hetzner, or GCP), then{' '}
                <strong>Get Started</strong> / <strong>Manage</strong>.
              </Step>
              <Step n={2} title="Add the account">
                For AWS, <UiPath parts={['Cloud Providers', 'Amazon Web Services', 'Add Account']} /> and enter the
                Account ID, name, an access key (or an <strong>Assume Role ARN</strong> for cross-account access), and
                the regions to scan. Prefer an assume-role over long-lived keys where possible.
                <CodeBlock
                  lang="text"
                  code={`Account ID:      123456789012
Account Name:    Production
Assume Role ARN: arn:aws:iam::123456789012:role/IAP-Access
Regions:         ap-south-1`}
                />
              </Step>
              <Step n={3} title="Discover & grant access">
                Once added, {BRAND.name} discovers resources in the account. Bind them to roles under <UiPath parts={['Roles']} />;
                users then request access under <UiPath parts={['Access Requests']} /> and open the console/resource
                through the gateway — fully audited, no shared credentials.
              </Step>
            </div>
            <Figure src={imgCloudProviders} caption="Cloud Providers: connect AWS, Azure, Hetzner, or GCP accounts" />
            <Figure src={imgCloudAddAws} caption="Add AWS Account: account ID, credentials/assume-role, and regions" />
          </GuideSection>

          {/* Verify */}
          <GuideSection {...SECTIONS[8]}>
            <ul className="space-y-2 text-sm text-[var(--d2d-text-secondary)]">
              <li className="flex gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--d2d-success)]" />
                <span>
                  <strong>Status = reachability.</strong> An agent resource shows <strong>Online</strong> only while its
                  agent holds a live hub connection — the same signal used to actually connect. If it's Online, connect
                  will work.
                </span>
              </li>
              <li className="flex gap-2">
                <Terminal className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--d2d-text-muted)]" />
                <span>
                  <strong>Agent won't come Online?</strong> Check the agent logs (<code>systemctl status iap-ssh-agent</code>{' '}
                  or <code>kubectl logs -l app=iap-k8s-agent</code>) for the token, hub URL, and connection messages.
                </span>
              </li>
              <li className="flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--d2d-warning)]" />
                <span>
                  <strong>Access denied on connect?</strong> The resource must be bound to a role the user holds, or the
                  user needs an approved request under <UiPath parts={['Access Requests']} />.
                </span>
              </li>
            </ul>
            <Callout kind="tip">
              All sessions (SSH, kubectl, RDP, DB) are logged under <UiPath parts={['Access Logs']} /> and{' '}
              <UiPath parts={['Sessions']} /> — recordings included where supported.
            </Callout>
          </GuideSection>
        </div>
      </div>
    </div>
  );
}
